/**
 * Parseur robuste pour extraire les FAQ depuis le contenu Markdown d'un article de blog.
 *
 * Détecte :
 *   - une section "## FAQ" / "## Foire aux questions" / "## Questions fréquentes"
 *   - les questions formatées en H3 (`### Question`) ou en gras (`**Question**`)
 *   - prend tout le texte jusqu'à la prochaine question ou la prochaine H2 comme réponse
 *
 * Génère un schema FAQPage (schema.org) prêt à injecter en JSON-LD,
 * pour obtenir des Rich Snippets Google (questions/réponses dans les SERP).
 */

const FAQ_HEADING_REGEX = /^(?:##\s+)?(?:FAQ|F\.A\.Q\.?|Foire aux questions|Questions fréquentes|Questions courantes|Questions[/-]r[ée]ponses)\b.*$/im;

/**
 * Strip Markdown syntax to produce a clean plain-text answer.
 */
function cleanMarkdown(text) {
  if (!text) return "";
  return text
    // Strip code fences
    .replace(/```[\s\S]*?```/g, "")
    // Strip inline code
    .replace(/`([^`]+)`/g, "$1")
    // Markdown links [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Bold/italic markers
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    // Headings remnants
    .replace(/^#{1,6}\s+/gm, "")
    // List markers
    .replace(/^[\s]*[-*+]\s+/gm, "")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    // Blockquotes
    .replace(/^>\s+/gm, "")
    // Collapse whitespace
    .replace(/\n{2,}/g, "\n\n")
    .replace(/[ \t]+/g, " ")
    .trim();
}

/**
 * Extract FAQ pairs from a markdown string.
 * @param {string} markdown
 * @returns {Array<{question: string, answer: string}>}
 */
export function extractFaqFromMarkdown(markdown) {
  if (!markdown || typeof markdown !== "string") return [];
  const lines = markdown.split("\n");

  // 1) Find FAQ section start (## FAQ ...)
  let startIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (FAQ_HEADING_REGEX.test(lines[i])) {
      startIdx = i + 1;
      break;
    }
  }
  if (startIdx === -1) return [];

  // 2) Find FAQ section end : next "## " heading OR a "natural" non-FAQ section heading
  let endIdx = lines.length;
  for (let i = startIdx; i < lines.length; i++) {
    const l = lines[i];
    // Markdown H2 (## Something) that's not a FAQ heading
    if (/^##\s+/.test(l) && !FAQ_HEADING_REGEX.test(l)) {
      endIdx = i;
      break;
    }
    // For plain-text articles (no markdown), stop at known section labels
    // ("En savoir plus", "Conclusion", "Liens internes", "Pour aller plus loin")
    if (/^(En savoir plus|Conclusion|Liens internes|Pour aller plus loin|Cas d['']usage)\b/i.test(l.trim())) {
      endIdx = i;
      break;
    }
  }

  const faqLines = lines.slice(startIdx, endIdx);
  const faqs = [];
  let current = null;

  // 3) Iterate, detecting question patterns
  const isQuestionLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    // ### Question
    if (/^###\s+.+/.test(trimmed)) return true;
    // **Question ?** (bold standalone with ? mark)
    if (/^\*\*[^*]+\?\*\*\s*$/.test(trimmed)) return true;
    // Line ending with ? on its own (not too long)
    if (/^.{4,200}\?$/.test(trimmed) && !trimmed.startsWith(">") && !trimmed.startsWith("-")) {
      // But only if next non-empty line is not a question itself
      return true;
    }
    return false;
  };

  const cleanQuestion = (line) => {
    return line
      .trim()
      .replace(/^###\s+/, "")
      .replace(/^\*\*([^*]+)\*\*\s*$/, "$1")
      .trim();
  };

  for (let i = 0; i < faqLines.length; i++) {
    const line = faqLines[i];
    if (isQuestionLine(line)) {
      if (current && current.answer.trim()) faqs.push(current);
      current = { question: cleanQuestion(line), answer: "" };
    } else if (current) {
      current.answer += line + "\n";
    }
  }
  if (current && current.answer.trim()) faqs.push(current);

  // 4) Clean answers (strip markdown, limit length)
  return faqs
    .map((f) => ({
      question: f.question.replace(/[?\s]+$/, "").trim() + " ?",
      answer: cleanMarkdown(f.answer).slice(0, 800),
    }))
    .filter((f) => f.question.length > 4 && f.answer.length > 10);
}

/**
 * Build a schema.org FAQPage JSON-LD payload from extracted FAQ pairs.
 * @param {Array<{question: string, answer: string}>} faqs
 * @returns {object}
 */
export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}
